import { GRID_COLS, GRID_ROWS, CELL, SCATTER, HUB_EXIT } from './config';

const key = (c, r) => `${c},${r}`;
const manhattan = (c1, r1, c2, r2) => Math.abs(c1 - c2) + Math.abs(r1 - r2);

function isWalkable(grid, c, r) {
  if (c < 0 || c >= GRID_COLS || r < 0 || r >= GRID_ROWS) return false;
  const t = grid[r][c];
  return t === CELL.CORRIDOR || t === CELL.POWERUP || t === CELL.TUNNEL;
}

// A* pathfinding
export function aStar(grid, sc, sr, tc, tr) {
  if (!isWalkable(grid, tc, tr)) return null;
  if (sc === tc && sr === tr) return [];

  const open = [{ c: sc, r: sr, g: 0, f: manhattan(sc, sr, tc, tr), parent: null }];
  const closed = new Set();

  while (open.length > 0) {
    // Find node with lowest f
    let bestIdx = 0;
    for (let i = 1; i < open.length; i++) {
      if (open[i].f < open[bestIdx].f || (open[i].f === open[bestIdx].f && Math.random() < 0.5)) {
        bestIdx = i;
      }
    }
    const cur = open.splice(bestIdx, 1)[0];
    const ck = key(cur.c, cur.r);

    if (cur.c === tc && cur.r === tr) {
      // Reconstruct path
      const path = [];
      let node = cur;
      while (node.parent) { path.unshift({ col: node.c, row: node.r }); node = node.parent; }
      return path;
    }

    closed.add(ck);

    for (const [dc, dr] of [[0, -1], [0, 1], [-1, 0], [1, 0]]) {
      const nc = cur.c + dc, nr = cur.r + dr;
      const nk = key(nc, nr);
      if (closed.has(nk) || !isWalkable(grid, nc, nr)) continue;

      const g = cur.g + 1;
      const existing = open.find(n => n.c === nc && n.r === nr);
      if (existing) {
        if (g < existing.g) { existing.g = g; existing.f = g + manhattan(nc, nr, tc, tr); existing.parent = cur; }
      } else {
        open.push({ c: nc, r: nr, g, f: g + manhattan(nc, nr, tc, tr), parent: cur });
      }
    }
  }
  return null;
}

// Get target tile per enemy type in CHASE mode
export function getChaseTarget(type, playerCol, playerRow, playerDir, blinkyCol, blinkyRow, selfCol, selfRow) {
  const dirOffsets = { up: [0, -1], down: [0, 1], left: [-1, 0], right: [1, 0] };
  const dir = dirOffsets[playerDir] || [0, 0];

  switch (type) {
    case 'blinky':
      return { col: playerCol, row: playerRow };
    case 'pinky':
      return { col: playerCol + dir[0] * 4, row: playerRow + dir[1] * 4 };
    case 'inky': {
      const aheadCol = playerCol + dir[0] * 2;
      const aheadRow = playerRow + dir[1] * 2;
      return { col: aheadCol + (aheadCol - blinkyCol), row: aheadRow + (aheadRow - blinkyRow) };
    }
    case 'clyde': {
      const dist = manhattan(selfCol !== undefined ? selfCol : playerCol, selfRow !== undefined ? selfRow : playerRow, playerCol, playerRow);
      if (dist > 8) return { col: playerCol, row: playerRow };
      return SCATTER.clyde;
    }
    default:
      return { col: playerCol, row: playerRow };
  }
}

// Clamp target to grid bounds
export function clampTarget(col, row) {
  return {
    col: Math.max(1, Math.min(GRID_COLS - 2, col)),
    row: Math.max(1, Math.min(GRID_ROWS - 2, row)),
  };
}

// Get scatter target for enemy type
export function getScatterTarget(type) {
  return SCATTER[type] || { col: 7, row: 1 };
}

// Find nearest walkable cell to target (for A* when target is wall)
export function findNearestWalkable(grid, tc, tr) {
  if (isWalkable(grid, tc, tr)) return { col: tc, row: tr };
  for (let d = 1; d <= 5; d++) {
    for (const [dc, dr] of [[0, -d], [0, d], [-d, 0], [d, 0], [-d, -d], [d, d], [-d, d], [d, -d]]) {
      const nc = tc + dc, nr = tr + dr;
      if (isWalkable(grid, nc, nr)) return { col: nc, row: nr };
    }
  }
  return HUB_EXIT; // fallback
}
